import React, { lazy, Suspense, useEffect } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { ROUTES, PAGE_METADATA, RoutePath } from "../Constants/Routes";
import LoadingScreen from "../Components/LoadingScreen/LoadingScreen";
import SEO from "../Components/SEO/SEO";
import { LayoutManager } from "../Layout/LayoutManager";
import NotFound from "../Pages/NotFound/NotFound";
import About from "../Pages/About/About";
import ContactUs from "../Pages/ContactUs/ContactUs";
import Services from "../Pages/Services/Services";
// import Training from "../Pages/Training/Training";
import Products from "../Pages/Products/Products";
import Terms from "../Pages/Terms/Terms";
import Privacy from "../Pages/PrivacyPolicy/PrivacyPolicy";
import Faq from "../Pages/FAQ/FAQ";
import Blogs from "../Pages/Blogs/Blogs";
import SingleBlogPost from "../Pages/SingleBlogPost/SingleBlogPost";

// Service Detail Pages - NO lazy loading
import TrainingService from "../Pages/Services/TrainingService";
import SolarService from "../Pages/Services/SolarService";
import HealthcareService from "../Pages/Services/HealthcareService";
import RealEstateService from "../Pages/Services/RealEstateService";

// Admin imports
import { AuthProvider } from "../admin/context/AuthContext";
import { ThemeProvider as AdminThemeProvider } from "../admin/context/ThemeContext";
import { ApiProvider } from "../admin/context/ApiContext";
import ProtectedRoute from "../admin/components/ProtectedRoute/ProtectedRoute";
import AdminLayout from "../admin/layouts/AdminLayout";
import AppsList from "../admin/pages/Apps/AppsList";
import BlogsList from "../admin/pages/Blogs/BlogsList";
import ContactsList from "../admin/pages/Contacts/ContactsList";
import AdminsList from "../admin/pages/Admins/AdminsList";
import Login from "../admin/pages/Login/Login";
import ForgotPassword from "../admin/pages/ForgotPassword/ForgotPassword";
import ResetPassword from "../admin/pages/ResetPassword/ResetPassword";
import Dashboard from "../admin/pages/Dashboard/Dashboard";
import ContactDetail from "../admin/pages/Contacts/ContactDetail";

const lazyWithDelay = <T extends React.ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  delay: number,
) =>
  lazy(() =>
    Promise.all([
      importFunc(),
      new Promise((resolve) => setTimeout(resolve, delay)),
    ]).then(([module]) => module),
  );

const LOADING_DURATION = 2000;

// Pages with delay - Only Home needs lazy loading for the loading screen
const Home = lazyWithDelay(
  () => import("../Pages/Home/Home"),
  LOADING_DURATION,
);

// Admin Theme Initializer Component
const AdminThemeInit: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  useEffect(() => {
    // Apply saved admin theme immediately
    const savedTheme = localStorage.getItem("admin-theme") || "light";
    document.documentElement.setAttribute("data-theme", savedTheme);
  }, []);

  return <>{children}</>;
};

const AppRoutes = () => {
  const wrap = (Page: React.FC, routeConfig: any) => {
    const metadata = PAGE_METADATA[routeConfig.path as RoutePath];
    return (
      <LayoutManager layoutType={routeConfig.layoutType}>
        <SEO
          title={metadata.title}
          description={metadata.description}
          keywords={metadata.keywords}
          ogImage={metadata.ogImage}
        />
        <Page />
      </LayoutManager>
    );
  };

  return (
    <Suspense
      fallback={
        <LoadingScreen
          duration={LOADING_DURATION}
          customMessage="LEARN AND INNOVATE"
          showProgress
        />
      }
    >
      <Routes>
        {/* Public Routes */}
        <Route path={ROUTES.HOME.path} element={wrap(Home, ROUTES.HOME)} />
        <Route path={ROUTES.ABOUT.path} element={wrap(About, ROUTES.ABOUT)} />
        <Route
          path={ROUTES.CONTACTUS.path}
          element={wrap(ContactUs, ROUTES.CONTACTUS)}
        />
        <Route path={ROUTES.BLOGS.path} element={wrap(Blogs, ROUTES.BLOGS)} />
        <Route
          path={`${ROUTES.BLOGS.path}/:slug`}
          element={wrap(SingleBlogPost, ROUTES.BLOG_POST)}
        />

        {/* Services Routes */}
        <Route
          path={ROUTES.SERVICES.path}
          element={wrap(Services, ROUTES.SERVICES)}
        />
        <Route
          path={ROUTES.TRAINING_SERVICE.path}
          element={wrap(TrainingService, ROUTES.TRAINING_SERVICE)}
        />
        <Route
          path={ROUTES.SOLAR_SERVICE.path}
          element={wrap(SolarService, ROUTES.SOLAR_SERVICE)}
        />
        <Route
          path={ROUTES.HEALTHCARE_SERVICE.path}
          element={wrap(HealthcareService, ROUTES.HEALTHCARE_SERVICE)}
        />
        <Route
          path={ROUTES.REAL_ESTATE_SERVICE.path}
          element={wrap(RealEstateService, ROUTES.REAL_ESTATE_SERVICE)}
        />

        {/* Other Public Routes */}
        {/* <Route
          path={ROUTES.TRAINING.path}
          element={wrap(Training, ROUTES.TRAINING)}
        /> */}
        <Route
          path={ROUTES.PRODUCTS.path}
          element={wrap(Products, ROUTES.PRODUCTS)}
        />
        <Route path={ROUTES.TERMS.path} element={wrap(Terms, ROUTES.TERMS)} />
        <Route
          path={ROUTES.PRIVACY.path}
          element={wrap(Privacy, ROUTES.PRIVACY)}
        />
        <Route path={ROUTES.FAQ.path} element={wrap(Faq, ROUTES.FAQ)} />

        {/* Admin Routes - Wrapped with Theme Provider */}
        <Route
          path="/admin/*"
          element={
            <AdminThemeInit>
              <AdminThemeProvider>
                <AuthProvider>
                  <ApiProvider>
                    <Routes>
                      {/* Public Admin Routes (No Auth Required) */}
                      <Route path="login" element={<Login />} />
                      <Route
                        path="forgot-password"
                        element={<ForgotPassword />}
                      />
                      <Route
                        path="reset-password"
                        element={<ResetPassword />}
                      />

                      {/* Protected Admin Routes */}
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <AdminLayout />
                          </ProtectedRoute>
                        }
                      >
                        {/* Redirect /admin to /admin/dashboard */}
                        <Route
                          index
                          element={<Navigate to="dashboard" replace />}
                        />
                        <Route path="dashboard" element={<Dashboard />} />
                        <Route path="apps" element={<AppsList />} />
                        <Route path="blogs" element={<BlogsList />} />
                        <Route path="contacts" element={<ContactsList />} />
                        <Route
                          path="contacts/:id"
                          element={<ContactDetail />}
                        />
                        <Route
                          path="admins"
                          element={
                            <ProtectedRoute requireSuperAdmin>
                              <AdminsList />
                            </ProtectedRoute>
                          }
                        />
                      </Route>
                    </Routes>
                  </ApiProvider>
                </AuthProvider>
              </AdminThemeProvider>
            </AdminThemeInit>
          }
        />

        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
